import { PlanInfoBadge } from "#/components/views/planinfo/PlanInfoBadge"
import React, { useMemo } from "react"
import { COMPANY_DEDUCTIBLE_STATUS, useUserDeductible } from "../../context/user/DeductibleContext"
import { FormattedMessage } from "react-intl"
import { reimbursementCategoryNameMessageKeys } from "../../views/reimbursements/reimbursementCategoryNameMessageKeys"
import { Box, BoxProps } from "@carrotfertility/carotene-core"
import { getCategoryCoverage } from "../../../utils/CategoryCoverage"
import { Subsidy, OutOfPocketMaxState } from "#/lib/carrot-api/types/Subsidy"

interface PlanInfoBadgeContainerProps {
  subsidy: Subsidy
  allSubsidies: Subsidy[]
  sx?: BoxProps["sx"]
  stackVertically?: boolean
}

type DeductibleModalType = "cmd" | "emd" | "emdAndOopm"

// Which plan-info dialogs (deductible, OOPM, cost share) apply to a subsidy.
interface PlanInfo {
  showDeductible: boolean
  showCostShare: boolean
  deductibleModalType: DeductibleModalType
  deductibleMayApply: boolean
  includesOopm: boolean
}

export function usePlanInfo(subsidy: Subsidy, allSubsidies: Subsidy[]): PlanInfo {
  const { companyDeductibleStatus, hasCMD } = useUserDeductible()
  const companyHasExternalDeductible = companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.EXTERNAL
  const companyHasCMD = companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.CARROT_MANAGED_DEDUCTIBLE
  const hasQmeCategory = getCategoryCoverage(allSubsidies).qme
  const categoryNameMessageKey = subsidy.reimbursementCategoryNameMessageKey.toString()
  const isOtherCategory = categoryNameMessageKey === reimbursementCategoryNameMessageKeys.OTHER
  const categorySupportsDeductibles =
    categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION &&
    categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.GC &&
    categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION_AND_GC &&
    !(isOtherCategory && hasQmeCategory)

  const hasOopmDependentRule = useMemo(
    () => subsidy.reimbursementRules?.some((rule) => rule.outOfPocketMaxState !== OutOfPocketMaxState.ANY) ?? false,
    [subsidy.reimbursementRules]
  )

  let deductibleModalType: DeductibleModalType = "emd"
  if (companyHasCMD) deductibleModalType = "cmd"
  else if (companyHasExternalDeductible && hasOopmDependentRule) deductibleModalType = "emdAndOopm"

  return {
    showDeductible: categorySupportsDeductibles && (companyHasExternalDeductible || (companyHasCMD && hasCMD)),
    showCostShare: Boolean(subsidy?.hasCostShare),
    deductibleModalType,
    deductibleMayApply: isOtherCategory && !hasQmeCategory,
    includesOopm: companyHasExternalDeductible && hasOopmDependentRule
  }
}

const PlanInfoBadgeContainer = ({
  subsidy,
  allSubsidies,
  sx,
  stackVertically = false
}: PlanInfoBadgeContainerProps): JSX.Element => {
  const {
    showDeductible: showDeductibleBadge,
    showCostShare: showCostShareBadge,
    deductibleModalType: deductibleBadgeModalType,
    deductibleMayApply,
    includesOopm
  } = usePlanInfo(subsidy, allSubsidies)

  let deductibleBadgeLabel = <FormattedMessage defaultMessage="Deductible applies" />
  if (deductibleMayApply) deductibleBadgeLabel = <FormattedMessage defaultMessage="Deductible may apply" />
  else if (includesOopm) deductibleBadgeLabel = <FormattedMessage defaultMessage="Deductible and OOPM apply" />

  if (!showDeductibleBadge && !showCostShareBadge) {
    return <></>
  }

  return (
    <Box
      display="flex"
      flexDirection={stackVertically ? "column" : "row"}
      gap={(theme) => theme.tokens.spacing.md}
      sx={sx}
    >
      {showDeductibleBadge && (
        <PlanInfoBadge subsidy={subsidy} modalType={deductibleBadgeModalType}>
          {deductibleBadgeLabel}
        </PlanInfoBadge>
      )}
      {showCostShareBadge && (
        <PlanInfoBadge subsidy={subsidy} modalType="costShare">
          <FormattedMessage defaultMessage="Cost sharing applies" />
        </PlanInfoBadge>
      )}
    </Box>
  )
}

export { PlanInfoBadgeContainer }
